import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Contact Source Marketing List page
 * Provides guided tours for common actions
 */
export const useContactSourceListQuickHelp = () => {
    const options: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'contact-source-sales-oncall',
                label: 'Lịch trực Sales',
                description: 'Xem ai đang trực hôm nay',
                selector: '[data-tour="sales-oncall"]',
                tourTitle: 'Lịch trực Sales',
                tourDescription:
                    'Hiển thị lịch trực Sales trong tuần và ai đang trực hôm nay.\n\n• Xem lịch trực theo tuần\n• Biết ai đang trực để phân công lead\n• Nếu bạn đang trực sẽ được highlight\n• Click vào tên để gọi điện/gửi email',
            },
            {
                id: 'contact-source-add-lead',
                label: 'Thêm Lead mới',
                description: 'Tạo nguồn liên hệ marketing mới',
                selector: '[data-tour="add-contact-btn"]',
                tourTitle: 'Thêm Lead mới',
                tourDescription:
                    'Nhấn nút này để tạo nguồn liên hệ marketing mới.\n\n• Nhập thông tin khách hàng tiềm năng\n• Phân công cho nhân viên phụ trách\n• Chọn kênh tiếp cận và ngành nghề',
            },
            {
                id: 'contact-source-import',
                label: 'Import nhiều Leads',
                description: 'Import leads từ file Excel',
                selector: '[data-tour="import-btn"]',
                tourTitle: 'Import Leads từ Excel',
                tourDescription:
                    'Sử dụng tính năng này để import nhiều leads cùng lúc.\n\n• Tải template mẫu để biết format\n• Điền thông tin leads vào file Excel\n• Upload và hệ thống sẽ tự động tạo leads',
            },
            {
                id: 'contact-source-filter',
                label: 'Lọc danh sách Leads',
                description: 'Hướng dẫn sử dụng từng bộ lọc',
                tourTitle: 'Bộ lọc Leads',
                tourDescription:
                    'Hướng dẫn sử dụng từng bộ lọc để tìm kiếm leads.',
                tourSteps: [
                    {
                        selector: '[data-tour="filter-search"]',
                        title: 'Tìm kiếm',
                        content:
                            'Tìm kiếm leads theo:\n• Tên người liên hệ\n• Email\n• Số điện thoại\n• Tên công ty',
                    },
                    {
                        selector: '[data-tour="filter-date"]',
                        title: 'Khoảng thời gian',
                        content:
                            'Lọc leads theo ngày tạo:\n• Hôm nay\n• 7 ngày qua\n• Tháng này\n• Tùy chọn khoảng ngày',
                    },
                    {
                        selector: '[data-tour="filter-status"]',
                        title: 'Trạng thái Lead',
                        content:
                            'Lọc theo trạng thái phân loại:\n• Warm Lead (MQL)\n• Cool Lead\n• Unqualified',
                        demo: 'lead-status-dropdown',
                    },
                    {
                        selector: '[data-tour="filter-assignment"]',
                        title: 'Trạng thái phân công',
                        content:
                            'Lọc theo tình trạng phân công:\n• Chờ phân công > 24 giờ\n• Đã phân công\n\nLưu ý: Filter này không dùng cùng với Status.',
                    },
                    {
                        selector: '[data-tour="filter-website"]',
                        title: 'Website nguồn',
                        content:
                            'Lọc theo website mà lead đến từ.\n\nVí dụ: vnetwork.vn, vnis.vn, vcloud.vn...',
                    },
                    {
                        selector: '[data-tour="filter-channel"]',
                        title: 'Kênh tiếp cận',
                        content:
                            'Lọc theo kênh marketing:\n• Website Form\n• Live Chat\n• Facebook\n• Hotline\n• Email\n• Sự kiện\n• Và nhiều kênh khác',
                    },
                    {
                        selector: '[data-tour="filter-product"]',
                        title: 'Nhóm sản phẩm',
                        content:
                            'Lọc theo dịch vụ mà lead quan tâm:\n• VNIS\n• VCLOUD\n• VCDN\n• Các dịch vụ khác',
                    },
                    {
                        selector: '[data-tour="filter-assignee"]',
                        title: 'Nhân viên phụ trách',
                        content:
                            'Lọc theo nhân viên được phân công.\n\nCó thể chọn nhiều người để xem leads của một nhóm.',
                    },
                ],
            },
            {
                id: 'contact-source-open-detail',
                label: 'Mở chi tiết Lead',
                description: 'Double-click để xem chi tiết',
                tourTitle: 'Mở chi tiết Lead',
                tourDescription:
                    'Double-click vào dòng trong bảng để mở modal chi tiết lead.\n\nModal hiển thị:\n• Thông tin liên hệ\n• Lịch sử thao tác\n• Chat history\n• Phân loại lead',
                tourSteps: [
                    {
                        selector: '[data-tour="lead-tabs"]',
                        title: 'Mở chi tiết Lead',
                        content:
                            'Double-click vào bất kỳ dòng nào trong bảng để mở modal chi tiết.\n\nTrong modal bạn có thể:\n• Xem/sửa thông tin\n• Phân loại lead (Warm/Cool/Unqualified)\n• Xem lịch sử chat\n• Theo dõi action log',
                        demo: 'lead-detail-modal',
                    },
                ],
            },
            {
                id: 'contact-source-classify',
                label: 'Phân loại Lead',
                description: 'Cách phân loại Warm/Cool/Unqualified',
                tourTitle: 'Phân loại Lead (BANT)',
                tourDescription:
                    'Phân loại lead dựa trên tiêu chí BANT:\n\n• Budget: Khả năng chi trả\n• Authority: Quyền quyết định mua hàng\n• Need: Nhu cầu sử dụng dịch vụ\n• Timeline: Thời gian triển khai\n\nWarm Lead: Quan tâm dịch vụ, có Authority\nCool Lead: Chưa có nhu cầu rõ ràng\nUnqualified: Không phù hợp tiêu chí',
            },
            {
                id: 'contact-source-convert',
                label: 'Chuyển đổi Lead → Customer',
                description: 'Quy trình convert lead thành khách hàng',
                tourTitle: 'Chuyển đổi Lead',
                tourDescription:
                    'Để chuyển lead thành Customer:\n\n1. Lead phải có status MATCHED\n2. Điền đầy đủ thông tin bắt buộc\n3. Click nút "Chuyển thành khách hàng"\n\nSau khi convert, lead sẽ được tạo thành Company/Contact trong hệ thống CRM.',
            },
            {
                id: 'contact-source-export',
                label: 'Xuất báo cáo Leads',
                description: 'Export danh sách leads ra Excel',
                selector: '[data-tour="export-btn"]',
                tourTitle: 'Export Leads',
                tourDescription:
                    'Xuất danh sách leads để phân tích hoặc báo cáo.\n\n• Dữ liệu xuất theo bộ lọc đang chọn\n• File Excel chứa đầy đủ thông tin leads\n• Có thể dùng để import vào hệ thống khác',
            },
            {
                id: 'contact-source-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang Leads',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn vào nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng của trang quản lý Leads.',
                tourSteps: [
                    {
                        selector: '[data-tour="sales-oncall"]',
                        title: 'Lịch trực Sales',
                        content:
                            'Hiển thị lịch trực Sales trong ngày để bạn biết ai đang trực và có thể phân công lead.',
                    },
                    {
                        selector: '[data-tour="leads-overview"]',
                        title: 'Tổng quan Leads',
                        content:
                            'Thống kê nhanh số lượng leads theo các trạng thái khác nhau.',
                    },
                    {
                        selector: '[data-tour="lead-filters"]',
                        title: 'Bộ lọc',
                        content:
                            'Sử dụng các bộ lọc để tìm kiếm leads theo nhiều tiêu chí.',
                    },
                    {
                        selector: '[data-tour="lead-tabs"]',
                        title: 'Tabs danh sách',
                        content:
                            'Chuyển đổi giữa "Leads của tôi", "Tất cả leads" và "Thống kê".',
                    },
                ],
            },
        ],
        []
    )

    useQuickHelp(options)

    return { options }
}

export default useContactSourceListQuickHelp
